// Info modal — preservation framing, attribution, and a peek at the
// project's nature. Opened by the "?" button (top-right) or the footer
// "about" link. Closed by clicking outside, the × button, or pressing
// Escape.

const COPY: ReadonlyArray<string> = [
  'A preservation project for the Cybiko Classic (2000) — a wireless handheld for teens that pioneered mesh-network chat between devices.',
  'The original CyOS firmware runs in browser through MAME-WASM, including the factory app catalog and bundled games such as Pinball Pro.',
  'Bring Your Own ROM/app files can be imported locally from the about modal. Files stay in this browser via IndexedDB and are not uploaded.',
  'The optional CyLM host prototype may send typed messages through a server-side model proxy for one reply. Provider credentials stay off the device page, and transcripts are not retained.',
  'Online experiments use private rooms or an opt-in public ephemeral CyRF room. Public discovery is disabled; chat bodies stay session-only; blocks, mutes, rate limits, and metadata-only reports apply. Local emulator flash remains separate and is not uploaded as account data.',
  'Not affiliated with or endorsed by any current Cybiko rights holder. Cybiko®, CyOS, applications, and the trade dress remain the property of their respective owners. Hosted for preservation purposes only.',
];

const HARDWARE: ReadonlyArray<readonly [string, string]> = [
  ['CPU', 'Hitachi H8S/2241 @ 11.0592 MHz'],
  ['Co-proc', 'Atmel AT90S2313 @ 4 MHz (RF)'],
  ['LCD', 'HD66421 — 160×100, 4-bit greyscale'],
  ['Radio', 'LMX2315 @ 902-928 MHz'],
  ['RAM', '256 KB'],
];

const LEGAL_LINKS: ReadonlyArray<readonly [string, string]> = [
  ['terms', '/legal/terms.html'],
  ['privacy', '/legal/privacy.html'],
  ['guidelines', '/legal/community.html'],
  ['copyright', '/legal/copyright.html'],
  ['online safety', '/legal/safety.html'],
];

export function initInfoModal(): void {
  const modal = document.getElementById('info-modal');
  if (!modal) return;

  const nativeRfRoom = nativeRfRoomPanel(window.location.href);
  modal.innerHTML = `
    <div class="modal-backdrop"></div>
    <div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="info-title">
      <button class="modal-close" type="button" aria-label="Close">&times;</button>
      <h2 id="info-title" class="modal-title">Cybiko</h2>
      ${COPY.map((p) => `<p class="modal-p">${p}</p>`).join('')}
      ${nativeRfRoom}
      <section id="byor-panel" class="modal-byor" aria-label="Bring Your Own ROMs and apps">
        <h3>Local ROMs</h3>
        <p>Loading local controls...</p>
      </section>
      <table class="modal-spec" aria-label="Hardware emulated">
        <caption>Hardware emulated</caption>
        ${HARDWARE.map(
          ([k, v]) => `<tr><th scope="row">${k}</th><td>${v}</td></tr>`,
        ).join('')}
      </table>
      <div class="modal-foot">
        <a href="https://github.com/meaux247/cybiko" target="_blank" rel="noopener noreferrer">source on github</a>
        <nav class="modal-legal-links" aria-label="Legal and safety documents">
          ${LEGAL_LINKS.map(([label, href]) => `<a href="${href}">${label}</a>`).join('')}
        </nav>
      </div>
    </div>
  `;

  const open = (): void => {
    modal.hidden = false;
    // Move focus into the modal for accessibility
    const closeBtn = modal.querySelector<HTMLButtonElement>('.modal-close');
    setTimeout(() => closeBtn?.focus(), 30);
  };
  const close = (): void => {
    modal.hidden = true;
  };

  const infoBtn = document.querySelector<HTMLElement>('.info-btn');
  infoBtn?.addEventListener('click', open);

  const link = document.getElementById('info-link');
  link?.addEventListener('click', (e) => {
    e.preventDefault();
    open();
  });

  modal
    .querySelector<HTMLElement>('.modal-close')
    ?.addEventListener('click', close);
  modal
    .querySelector<HTMLElement>('.modal-backdrop')
    ?.addEventListener('click', close);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hidden) {
      close();
      e.preventDefault();
      e.stopPropagation();
    }
  });
}

function nativeRfRoomPanel(rawUrl: string): string {
  const url = new URL(rawUrl);
  const params = url.searchParams;
  const mode = params.get('nativeRfChat');
  if (mode !== 'online' && mode !== 'public') return '';

  const isPublicRoom = mode === 'public';
  const room = isPublicRoom ? 'public' : cleanRoomLabel(params.get('room') ?? 'native-rf-chat-local-room');
  const rfName = cleanNameLabel(params.get('rfName') ?? 'CYBIKO');
  const invite = isPublicRoom ? 'not used' : (params.has('onlineInvite') ? 'set' : 'not set');
  const peerUrl = new URL(url);
  peerUrl.searchParams.set('nativeRfChat', mode);
  if (isPublicRoom) {
    peerUrl.searchParams.delete('room');
    peerUrl.searchParams.delete('onlineInvite');
  } else {
    peerUrl.searchParams.set('room', room);
  }
  peerUrl.searchParams.set('rfName', rfName === 'ALICE' ? 'BOB' : 'ALICE');

  return `
    <section class="modal-rf-room" aria-label="Native RF Chat room">
      <h3>Native RF room</h3>
      <dl>
        <div><dt>room</dt><dd>${escapeHtml(room)}</dd></div>
        <div><dt>name</dt><dd>${escapeHtml(rfName)}</dd></div>
        <div><dt>invite</dt><dd>${invite}</dd></div>
      </dl>
      <a href="${escapeAttribute(peerUrl.toString())}">open peer session</a>
    </section>
  `;
}

function cleanRoomLabel(value: string): string {
  return value
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48) || 'native-rf-chat-local-room';
}

function cleanNameLabel(value: string): string {
  return value
    .normalize('NFKC')
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, '')
    .slice(0, 8) || 'CYBIKO';
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function escapeAttribute(value: string): string {
  return escapeHtml(value);
}
